<template>
    <div class="chart-grid">
        <div class="chart-card">
            <div class="chart-title">Zamówienia dzienne ({{ rangeLabel }})</div>
            <apexchart type="line" height="300" :options="ordersChartOptions" :series="ordersChartSeries" />
            <div class="chart-legend">
                <span>Min: {{ dailyStats.min }}</span>
                <span>Max: {{ dailyStats.max }}</span>
                <span>Śr: {{ dailyStats.avg.toFixed(1) }}</span>
            </div>
        </div>

        <div class="chart-card">
            <div class="chart-title">TOP 5 modeli ({{ rangeLabel }})</div>
            <apexchart type="bar" height="300" :options="topChartOptions" :series="topChartSeries" />
        </div>
    </div>
</template>

<script>
import { useOrderStore } from '@/stores/orderStore'
import { useTrailerStore } from '@/stores/trailerStore'

export default {
    props: {
        selectedRange: { type: String, required: true },
        themeColor: { type: String, default: '#899c5f' },
        themeAccent: { type: String, default: '#899c5f' },
    },
    data() {
        return {
            orderStore: useOrderStore(),
            trailerStore: useTrailerStore(),
        }
    },
    computed: {
        orders() { return this.orderStore.orders || [] },

        trailerMap() {
            const m = new Map()
            for (const t of this.trailerStore.trailers) m.set(t.id, t)
            return m
        },
        rangeLabel() {
            const map = {
                '7d': '7 dni', '30d': '30 dni', '90d': '90 dni',
                '180d': '180 dni', '1y': 'Ostatni rok',
                'ytd': 'Bieżący rok', 'all': 'Wszystko'
            }
            return map[this.selectedRange] || '—'
        },
        fromDate() {
            const now = new Date()
            const clampStart = (days) => {
                const d = new Date()
                d.setDate(d.getDate() - (days - 1))
                d.setHours(0, 0, 0, 0)
                return d
            }
            if (this.selectedRange === '7d') return clampStart(7)
            if (this.selectedRange === '30d') return clampStart(30)
            if (this.selectedRange === '90d') return clampStart(90)
            if (this.selectedRange === '180d') return clampStart(180)
            if (this.selectedRange === '1y') {   
                const d = new Date()
                d.setFullYear(d.getFullYear() - 1)
                d.setHours(0, 0, 0, 0)
                return d
            }
            if (this.selectedRange === 'ytd') {
                const d = new Date(now.getFullYear(), 0, 1)
                d.setHours(0, 0, 0, 0)
                return d
            }
            const minDate = this.orders.length
                ? new Date(Math.min(...this.orders.map(o => +new Date(o.created_at))))
                : clampStart(30)
            minDate.setHours(0, 0, 0, 0)
            return minDate
        },
        ordersInRange() {
            return this.orders.filter(o => new Date(o.created_at) >= this.fromDate)
        },
        dailySeries() {
            const start = this.fromDate
            const today = new Date()
            const days = Math.max(1, Math.ceil((today - start) / (24 * 3600 * 1000)) + 1)

            const buckets = Array.from({ length: days }).map((_, i) => ({
                date: new Date(start.getTime() + i * 24 * 3600 * 1000),
                count: 0
            }))

            for (const o of this.ordersInRange) {
                const idx = Math.floor((new Date(o.created_at) - start) / (24 * 3600 * 1000))
                if (idx >= 0 && idx < buckets.length) buckets[idx].count++
            }
            return buckets
        },
        dailyStats() {
            const arr = this.dailySeries.map(d => d.count)
            const min = arr.length ? Math.min(...arr) : 0
            const max = arr.length ? Math.max(...arr) : 0
            const avg = arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0
            return { min, max, avg }
        },

        ordersChartSeries() {
            return [{ name: 'Zamówienia', data: this.dailySeries.map(d => d.count) }]
        },
        ordersChartOptions() {
            return {
                chart: { id: 'orders-range', toolbar: { show: false }, animations: { enabled: true } },
                theme: { monochrome: { enabled: false } },
                colors: [this.themeColor],
                stroke: { curve: 'smooth', width: 3, colors: [this.themeColor] },
                fill: { type: 'solid', colors: [this.themeColor], opacity: 1 },
                markers: { size: 0, colors: [this.themeColor], strokeColors: this.themeColor },
                grid: { strokeDashArray: 4 },
                xaxis: {
                    categories: this.dailySeries.map(d =>
                        d.date.toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit' })
                    ),
                    tickAmount: 6
                },
                yaxis: { min: 0, forceNiceScale: true, labels: { formatter: v => Math.round(v).toString() } },
                tooltip: { y: { formatter: val => `${val} szt.` } }
            }
        },
        top5Trailers() {
            const m = new Map()
            for (const o of this.ordersInRange) m.set(o.trailer_id, (m.get(o.trailer_id) || 0) + 1)
            return [...m.entries()]
                .map(([id, count]) => ({ id, count }))
                .sort((a, b) => b.count - a.count)
                .slice(0, 5)
        },
        topChartSeries() {
            return [{ name: 'Zamówienia', data: this.top5Trailers.map(r => r.count) }]
        },
        topChartOptions() {
            return {
                chart: { toolbar: { show: false } },
                theme: { monochrome: { enabled: false } },
                colors: [this.themeAccent],
                fill: { type: 'solid', colors: [this.themeAccent], opacity: 1 },
                stroke: { show: true, width: 0, colors: [this.themeAccent] },
                plotOptions: { bar: { horizontal: false, borderRadius: 6, columnWidth: '45%' } },
                dataLabels: { enabled: false },
                grid: { strokeDashArray: 4 },
                xaxis: { categories: this.top5Trailers.map(r => this.trailerMap.get(r.id)?.name || '—') },
                yaxis: { min: 0, forceNiceScale: true, labels: { formatter: v => Math.round(v).toString() } },
                tooltip: { y: { formatter: val => `${val} szt.` } }
            }
        },
    },
    created() {
        if (!this.orderStore.orders?.length) this.orderStore.fetchOrders?.()
        if (!this.trailerStore.trailers?.length) this.trailerStore.fetchTrailers?.()
    }
}
</script>

<style scoped lang="scss">
.chart-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    margin: 1rem 0 1.5rem;

    @media (max-width: 900px) {
        grid-template-columns: 1fr;
    }
}

.chart-card {
    background: $color-white;
    border: 1px solid $color-grey;
    border-radius: 10px;
    padding: 20px;
    min-width: 0;
}

.chart-title {
    font-weight: 600;
    margin-bottom: .5rem;
    color: $color-text;
}

.chart-legend {
    display: flex;
    gap: 1rem;
    font-size: .85rem;
    color: $color-text-light;
    margin-top: .25rem;
}
</style>